<template>
    <div data-testid="pedido-card" class="tw:bg-white tw:rounded-lg tw:shadow tw:overflow-hidden">
        <!-- Match header (our design) -->
        <div v-if="isMatch" @click="toggleOpen" data-testid="pedido-card-header" class="tw:relative tw:h-44 tw:w-full tw:cursor-pointer tw:overflow-hidden" :style="headerBgStyle">
            <div class="tw:absolute tw:inset-0" style="background: radial-gradient(circle at center, #796f96 0%, #685d8a 50%, #4e4471 100%);">
                <Matchup
                    :local-team="pedido.evento.local"
                    :away-team="pedido.evento.visitante"
                    compact
                    hide-division
                    transparent
                    text-color="white"
                />
                <div class="tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:flex tw:justify-between tw:items-end tw:px-4 tw:py-3">
                    <p v-if="pedido.fecha_evento" class="tw:text-white/80 tw:text-xs tw:flex tw:items-center tw:gap-1.5 tw:font-medium tw:m-0">
                        <i class="material-icons tw:text-xs">calendar_month</i>
                        {{ formatEventDate(pedido.fecha_evento) }}
                    </p>
                    <div class="tw:bg-white/10 tw:backdrop-blur-md tw:px-3 tw:py-1.5 tw:rounded-lg tw:border tw:border-white/20 tw:text-right">
                        <p class="tw:text-[10px] tw:text-white/70 tw:uppercase tw:font-bold tw:tracking-widest tw:leading-none tw:mb-0.5 tw:m-0">{{ $t('perfil.pedido') }}</p>
                        <p class="tw:text-white tw:font-mono tw:font-bold tw:text-sm tw:m-0">#{{ pedido.id }}</p>
                    </div>
                </div>
            </div>
            <i class="material-icons tw:absolute tw:top-3 tw:right-3 tw:text-white/60 tw:text-xl tw:transition-transform tw:duration-300" :class="{ 'tw:rotate-180': isOpen }">expand_more</i>
        </div>
        <!-- Event header (non-match: imagen or randomColor) -->
        <div v-else @click="toggleOpen" class="tw:relative tw:h-44 tw:w-full tw:cursor-pointer tw:overflow-hidden" :style="headerBgStyle">
            <div class="tw:absolute tw:inset-0 tw:bg-gradient-to-t tw:from-gray-900/70 tw:via-gray-900/20 tw:to-transparent tw:flex tw:flex-col tw:justify-end tw:p-4">
                <div class="tw:flex tw:justify-between tw:items-end">
                    <div class="tw:min-w-0 tw:flex-1 tw:pr-2">
                        <h2 class="tw:text-xl tw:font-bold tw:text-white tw:uppercase tw:m-0 tw:truncate">{{ pedido.evento?.nombre }}</h2>
                        <p v-if="pedido.fecha_evento" class="tw:text-slate-200 tw:text-sm tw:flex tw:items-center tw:tracking-wide tw:gap-1.5 tw:mt-1 tw:m-0">
                            <i class="material-icons tw:text-sm">calendar_month</i>
                            {{ formatEventDate(pedido.fecha_evento) }}
                        </p>
                    </div>
                    <div class="tw:bg-white/10 tw:backdrop-blur-md tw:px-3 tw:py-1.5 tw:rounded-lg tw:border tw:border-white/20 tw:text-right tw:flex-shrink-0 tw:ml-3">
                        <p class="tw:text-[10px] tw:text-white/70 tw:uppercase tw:font-bold tw:tracking-widest tw:leading-none tw:mb-0.5 tw:m-0">{{ $t('perfil.pedido') }}</p>
                        <p class="tw:text-white tw:font-mono tw:font-bold tw:text-sm tw:m-0">#{{ pedido.id }}</p>
                    </div>
                </div>
            </div>
            <i class="material-icons tw:absolute tw:top-3 tw:right-3 tw:text-white/60 tw:text-xl tw:transition-transform tw:duration-300" :class="{ 'tw:rotate-180': isOpen }">expand_more</i>
        </div>

        <transition name="fade">
            <div v-if="isOpen" class="tw:border-t tw:border-slate-100">

                <!-- Loading skeleton -->
                <div v-if="loadingDetalle" class="tw:p-4 tw:space-y-3">
                    <!-- Botones de acción principales -->
                    <div class="tw:flex tw:flex-wrap tw:gap-2">
                        <SkeletonItem customClass="tw:rounded-lg" height="tw:h-10" class="tw:w-40" />
                        <SkeletonItem customClass="tw:rounded-lg" height="tw:h-10" class="tw:w-40" />
                    </div>
                    <!-- Label de sección tickets -->
                    <SkeletonItem customClass="tw:w-42 tw:mt-7 tw:mb-7" height="tw:h-6" />
                    <!-- Una fila con los 5 bloques de la entrada -->
                    <div class="tw:bg-slate-50 tw:rounded-lg tw:p-4 tw:border tw:border-slate-100 tw:space-y-3">
                        <div class="tw:flex tw:items-start tw:gap-12 tw:flex-wrap tw:md:flex-nowrap tw:ml-5">
                            <div class="tw:flex tw:flex-col tw:gap-1">
                                <SkeletonItem customClass="tw:w-20" height="tw:h-3" />
                                <SkeletonItem customClass="tw:w-40" height="tw:h-5" />
                                <SkeletonItem customClass="tw:w-28" height="tw:h-4" />
                            </div>
                            <div class="tw:flex tw:flex-col tw:gap-2">
                                <SkeletonItem customClass="tw:w-20" height="tw:h-3" />
                                <SkeletonItem customClass="tw:w-32" height="tw:h-5" />
                            </div>
                            <div class="tw:flex tw:flex-col tw:gap-1">
                                <SkeletonItem customClass="tw:w-20" height="tw:h-3" />
                                <SkeletonItem customClass="tw:w-32" height="tw:h-5" />
                                <SkeletonItem customClass="tw:w-24" height="tw:h-4" />
                            </div>
                            <div class="tw:flex tw:flex-col tw:gap-2">
                                <SkeletonItem customClass="tw:w-20" height="tw:h-3" />
                                <SkeletonItem customClass="tw:w-16 tw:rounded-full" height="tw:h-6" />
                            </div>
                            <div class="tw:flex tw:flex-col tw:gap-2">
                                <SkeletonItem customClass="tw:w-20" height="tw:h-3" />
                                <SkeletonItem customClass="tw:w-16 tw:rounded-full" height="tw:h-6" />
                            </div>
                        </div>
                        <!-- Botones de acción de la entrada -->
                        <div class="tw:flex tw:gap-2 tw:pt-2 tw:border-t tw:border-slate-100 tw:flex-wrap tw:ml-5">
                            <SkeletonItem customClass="tw:rounded" height="tw:h-8 tw:w-28" />
                            <SkeletonItem customClass="tw:rounded" height="tw:h-8 tw:w-24" />
                            <SkeletonItem customClass="tw:rounded" height="tw:h-8 tw:w-24" />
                            <SkeletonItem customClass="tw:rounded" height="tw:h-8 tw:w-24" />
                        </div>
                    </div>
                </div>

                <!-- Error state -->
                <div v-else-if="errorDetalle" class="tw:p-4">
                    <p class="tw:text-sm tw:text-red-500 tw:m-0">
                        <i class="material-icons tw:text-sm tw:align-middle">error_outline</i>
                        {{ errorDetalle }}
                    </p>
                </div>

                <!-- Detail content -->
                <template v-else-if="detalle">
                    <div v-if="!todasAnuladas" class="tw:p-4 tw:flex tw:flex-wrap tw:gap-4 tw:border-b tw:border-slate-100">
                        <Button data-testid="pedido-card-btn-descargar" variant="alt" v-if="detalle.pdf_url && puedeDescargarEntradas" as="a" :href="detalle.pdf_url" target="_blank" rel="noopener">
                            <i class="material-icons tw:text-sm">download</i> {{ $t('perfil.descargar_todas_btn') }}
                        </Button>
                        <Button
                            variant="alt"
                            data-testid="pedido-card-btn-enviar-entradas"
                            @click="emit('enviar-entradas', { ...pedido, entradas: detalle.entradas })"
                        >
                            <i class="material-icons tw:text-sm">send</i> {{ $t('perfil.enviar_entradas') }}
                        </Button>
                        <Button
                            v-if="pedido.evento?.solicitar_datos && !sinCosteDeCambio"
                            variant="alt"
                            data-testid="pedido-card-btn-cambiar-datos"
                            @click="emit('cambiar-datos', { ...pedido, entradas: detalle.entradas })"
                        >
                            <i class="material-icons tw:text-sm">edit</i> {{ $t('perfil.cambiar_datos') }}
                            <PopoverFront v-if="detalle.entradas?.[0]?.fecha_limite_cambiar_datos" position="top">
                                <template #trigger>
                                    <i class="material-icons tw:text-xs tw:ml-1 tw:opacity-70">info</i>
                                </template>
                                <p class="tw:m-0 tw:text-xs">
                                    {{ $t('perfil.limite_cambiar_datos', { fecha_fin_evento: detalle.entradas[0].fecha_limite_cambiar_datos.nombre }) }}
                                </p>
                            </PopoverFront>
                        </Button>
                    </div>

                    <div class="tw:p-4 tw:space-y-3">
                        <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-widest tw:flex tw:items-center tw:gap-2 tw:m-0 tw:pb-3">
                            <i class="material-icons tw:text-sm">local_activity</i> {{ $t('perfil.tickets_pedido') }}
                        </p>
                        <PedidoEntradaRow
                            v-for="(entrada, i) in detalle.entradas || []"
                            :key="entrada.id"
                            :entrada="entrada"
                            :pedido="pedido"
                            :index="i"
                            :todas-entradas="detalle.entradas"
                            :puede-descargar-entradas="puedeDescargarEntradas"
                            @edit-success="emit('edit-success')"
                        />
                    </div>

                    <div v-if="detalle.products && detalle.products.length > 0" class="tw:px-4 tw:pb-4">
                        <div class="tw:flex tw:flex-col tw:my-3 tw:gap-3">
                            <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-widest tw:flex tw:items-center tw:gap-2 tw:m-0">
                                <i class="material-icons tw:text-sm">fastfood</i> {{ $t('perfil.productos') }}
                            </p>
                            <Button v-if="detalle.products_pdf" variant="default-tiny" as="a" :href="detalle.products_pdf" target="_blank" rel="noopener" class="tw:w-50">
                                <i class="material-icons tw:text-sm">download</i> {{ $t('perfil.descargar_productos_pdf') }}
                            </Button>
                        </div>
                        <div class="tw:grid sm:tw:grid-cols-2 tw:gap-3">
                            <ProductoCard
                                v-for="producto in detalle.products"
                                :key="producto.id"
                                :producto="producto"
                            />
                        </div>
                    </div>
                </template>
            </div>
        </transition>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import { usePedidoDetalle } from '@/composables/front/perfil/usePedidoDetalle';
    import { randomColor } from '@/utils/randomColor';
    import { formatEventDate } from '@/utils/dateUtils';
    import PedidoEntradaRow from './PedidoEntradaRow.vue';
    import ProductoCard from './ProductoCard.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import PopoverFront from '@/modules/front/ui/PopoverFront.vue';
    import Matchup from './cesion/Matchup.vue';
    import { SkeletonItem } from '@/modules/front/ui/skeleton';

    const props = defineProps({
        pedido: { type: Object, required: true },
    });

    const emit = defineEmits(['cambiar-datos', 'enviar-entradas', 'edit-success']);

    const isOpen = ref(false);
    const isMatch = computed(() => !!props.pedido?.evento?.local);

    const headerBgStyle = computed(() => {
        if (isMatch.value) return {};
        const img = props.pedido?.evento?.imagen;
        if (img) {
            return {
                backgroundImage: `url(${img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            };
        }
        const color = randomColor(props.pedido.id ?? 0);
        return { backgroundColor: color.iconColor };
    });

    const { detalle, loading: loadingDetalle, error: errorDetalle, fetchDetalle } = usePedidoDetalle(props.pedido.id);

    const todasAnuladas = computed(() => detalle.value?.entradas?.every(e => !!e.deleted_at) ?? false);
    const sinCosteDeCambio = computed(() => Number(props.pedido?.evento?.coste_cambio_nombre) === 0);
    const puedeDescargarEntradas = computed(() => detalle.value?.puede_descargar_entradas ?? true);

    function toggleOpen() {
        isOpen.value = !isOpen.value;
    }

    watch(isOpen, open => {
        if (open) fetchDetalle();
    });
</script>
